/**
 * 前端打印模块封装(仅支持新壳子)
 */

import { clone } from '@utiljs/clone';

/**
 * 本地存储中用于保存打印机配置的 key
 */
const SETTINGS_KEY = 'printer_settings';

const appInfo = window.wmSystemApi && window.wmSystemApi.getAppInfo();

if (!appInfo || appInfo.code < 4000 || !window.wmPrinterApi) {
    console.error('请在新壳子中使用此模块');
}

interface ITicketsCount {
    /** 商家联 */
    '1': number;
    /** 后厨联 */
    '2': number;
    /** 顾客联 */
    '3': number;
    /** 配送联 */
    '4': number;
}

/**
 * 具体打印机的设置
 */
interface ISinglePrinterSettings {
    id: string;
    enable: boolean;
    orderCount: number;
    orderInterval: number;
    inshopCount: number;
    inshopInterval: number;
    inshopAutoPrint: boolean;
    pageWidth: number | string;
    cmdModel: string;
    orderType: string;
    ticketsCount?: ITicketsCount;
}

/**
 * 门店打印设置对象
 */
interface IPoiPrinterSettings {
    list: {
        [index: string]: ISinglePrinterSettings;
    };
    /**
     * 是否默认模式  0:默认模式  1:自定义模式
     */
    model: number;
    /**
     * 是否默认设置  0:默认设置  1:单独设置
     */
    useModel: number;
    hasAutoCheckDefaultPrinter?: boolean;
}

interface IAllPoiPrinterSettings {
    [index: string]: IPoiPrinterSettings;
}

let SETTINGS_CACHE: IAllPoiPrinterSettings | null = null;

/**
 * 获取全部当前系统连接的打印机列表
 */
const getAllPrinters = () => {
    return (
        (window.wmPrinterApi &&
            window.wmPrinterApi.getAllPrinters &&
            window.wmPrinterApi.getAllPrinters()) ||
        []
    );
};

/**
 * 获取默认/推荐打印机
 */
const getDefaultPrinter = (): PrinterLocal | null => {
    const allPrinters = getAllPrinters();

    // 优先使用 USB 打印机
    const usbPrinters = allPrinters.filter(printer => printer.type === 'usb');

    if (usbPrinters.length > 0) {
        return usbPrinters[0];
    }

    // 判断 GDI 打印机
    const defaultPrinter = window.wmPrinterApi!.getDefaultPrinter();
    const gdiPrinters = allPrinters.filter(printer => printer.type === 'printer');

    let printer = gdiPrinters[0] || null;
    if (defaultPrinter && defaultPrinter.name) {
        gdiPrinters.forEach(p => {
            if (p.name === defaultPrinter.name) {
                printer = p;
            }
        });
    }

    return printer;
};

/**
 * 为总店创建默认的配置对象
 */
const createDefaultSettingsForAllPoi = () => {
    return {
        list: {},
        model: 0,
        useModel: 0,
    };
};

/**
 * 保存配置到设备
 */
const saveSettings = () => {
    window.wmSystemApi!.setStorage(SETTINGS_KEY, JSON.stringify(SETTINGS_CACHE));
};

/**
 * 为指定打印机创建默认配置
 * @param printerId 打印机ID
 */
const createDefaultPrinterSettings = (printerId: string): ISinglePrinterSettings => {
    return {
        id: printerId,
        enable: false,
        orderCount: 1,
        orderInterval: 1,
        inshopCount: 1,
        inshopInterval: 1,
        inshopAutoPrint: true,
        pageWidth: 0,
        cmdModel: 'default',
        orderType: 'default',
    };
};

/**
 * 获取全部门店的打印机设置
 */
const getAllPoiSettings = (): IAllPoiPrinterSettings => {
    if (!SETTINGS_CACHE) {
        const settingStr = window.wmSystemApi!.getStorage(SETTINGS_KEY) || '';
        let settingObj;
        try {
            settingObj = JSON.parse(settingStr);
        } catch (e) {
            settingObj = null;
        }
        if (settingObj === null || settingStr === '') {
            settingObj = {
                '-1': createDefaultSettingsForAllPoi(),
            };

            window.wmSystemApi!.setStorage(SETTINGS_KEY, JSON.stringify(settingObj));
        }
        if (settingObj['-1'] === undefined) {
            settingObj['-1'] = createDefaultSettingsForAllPoi();
            window.wmSystemApi!.setStorage(SETTINGS_KEY, JSON.stringify(settingObj));
        }

        SETTINGS_CACHE = settingObj;

        return settingObj;
    }
    return SETTINGS_CACHE;
};

/**
 * 基于默认打印机创建门店打印设置对象
 *
 * @param ptSetting 默认打印机设置
 */
const createPoiSettingsForSignlePrinter = (ptSetting?: ISinglePrinterSettings) => {
    if (!ptSetting) {
        return {
            list: {},
            useModel: 1,
            model: 1,
        };
    }
    return {
        list: {
            [ptSetting.id]: { ...clone(ptSetting), enable: true }, // 启用默认打印机
        },
        useModel: 1,
        model: 1,
    };
};

/**
 * 获取单个门店的打印机设置
 */
const getSinglePoiSettings = (poiId: string): IPoiPrinterSettings => {
    const allSettings = getAllPoiSettings();

    let poiSettings = allSettings[poiId];
    const defaultPrinter = getDefaultPrinter();
    if (poiSettings === undefined || poiSettings.useModel === 0) {
        // 从总店同步配置
        const defaultSettings = allSettings['-1'];
        if (defaultSettings.model === 0) {
            if (!defaultPrinter) {
                poiSettings = createPoiSettingsForSignlePrinter();
            } else {
                // 默认模式，使用系统推荐打印机
                const ptSetting =
                    defaultSettings.list[defaultPrinter.id] ||
                    createDefaultPrinterSettings(defaultPrinter.id);
                poiSettings = createPoiSettingsForSignlePrinter(ptSetting);
            }
        } else {
            // 自定义模式
            poiSettings = {
                list: Object.keys(defaultSettings.list)
                    .filter(ptId => defaultSettings.list[ptId].enable)
                    .reduce((acc, ptId) => {
                        acc[ptId] = clone(defaultSettings.list[ptId]);
                        return acc;
                    }, {} as { [index: string]: ISinglePrinterSettings }),
                useModel: 1,
                model: 1,
            };
        }
        allSettings[poiId] = poiSettings;
        saveSettings();
    } else if (poiSettings.useModel === 1 && poiSettings.model === 0) {
        if (!defaultPrinter) {
            poiSettings = createPoiSettingsForSignlePrinter();
        } else {
            // 单独设置+默认模式转换为单独设置+自定义模式
            const ptSetting =
                poiSettings.list[defaultPrinter.id] ||
                createDefaultPrinterSettings(defaultPrinter.id);
            poiSettings = createPoiSettingsForSignlePrinter(ptSetting);
        }
        allSettings[poiId] = poiSettings;
        saveSettings();
    }

    return poiSettings;
};

/**
 * 获取指定门店下，指定打印机的设置。如果没有，则创建默认设置并返回
 */
const getPoiSinglePrinterSettings = (poiId: string, printerId: string): ISinglePrinterSettings => {
    const poiSettings = getSinglePoiSettings(poiId);

    let printerSettings = poiSettings.list[printerId];

    if (printerSettings === undefined) {
        // 创建默认设置
        printerSettings = createDefaultPrinterSettings(printerId);
        poiSettings.list[printerId] = printerSettings;

        saveSettings();
    }

    return printerSettings;
};

/**
 * 修改门店下特定打印机的配置项
 *
 * @param poiId 门店ID
 * @param printerId 打印机ID
 * @param key 修改的设置项
 * @param value 要修改成的值
 */

interface IChangePoiSinglePrinterSettings {
    (poiId: string, printerId: string, key: 'enable', value: boolean): void;
    (poiId: string, printerId: string, key: 'pageWidth', value: number): void;
    (poiId: string, printerId: string, key: 'ticketsCount', value: ITicketsCount): void;
}

const changePoiSinglePrinterSettings: IChangePoiSinglePrinterSettings = (
    poiId: string,
    printerId: string,
    key: string,
    value: any,
) => {
    const ptSetting = getPoiSinglePrinterSettings(poiId, printerId);
    if (key === 'enable') {
        ptSetting[key] = value;
    }
    if (key === 'pageWidth') {
        ptSetting[key] = value;
    }
    if (key === 'ticketsCount') {
        ptSetting[key] = value;
    }

    saveSettings();
};

const clearSettingsCache = () => {
    SETTINGS_CACHE = null;
};

const charWidthOfPageWidth = (pageWidth: number | string) => {
    let pageWidthN: number;
    if (typeof pageWidth === 'string') {
        pageWidthN = parseInt(pageWidth, 10);
    } else {
        pageWidthN = pageWidth;
    }
    const w = 58 / 32.0;
    return Math.floor(pageWidthN / w);
};

/** 获取门店启动的且当前连接的打印机 */
const getPoiEnabledConnectedPrinters = (wmPoiId: string): ISinglePrinterSettings[] => {
    // 门店打印机设置
    const poiPrinterSetting = getSinglePoiSettings(wmPoiId);
    // 门店启用的打印机列表
    const poiEnabledPrinters = Object.keys(poiPrinterSetting.list)
        .map(printerId => poiPrinterSetting.list[printerId])
        .filter(printer => printer.enable);
    // 当前系统连接的打印机
    const systemConnectedPrinters = getAllPrinters();
    // 门店启动的且当前连接系统的打印机列表
    const poiEnabledConnectedPrinters = poiEnabledPrinters.filter(p => {
        return systemConnectedPrinters.filter(cp => cp.id === p.id).length > 0;
    });

    return poiEnabledConnectedPrinters;
};

export {
    IAllPoiPrinterSettings,
    IPoiPrinterSettings,
    ISinglePrinterSettings,
    ITicketsCount,
    getAllPrinters,
    getDefaultPrinter,
    getAllPoiSettings,
    getPoiSinglePrinterSettings,
    changePoiSinglePrinterSettings,
    clearSettingsCache,
    charWidthOfPageWidth,
    getSinglePoiSettings,
    getPoiEnabledConnectedPrinters,
};
